// What the logged in admin can see / do in the LMS screens.
// The API sends it as lms_access on admin login and from lms/admin/me:
// { role: super_admin|sub_admin|null, permissions: ["lms_dashboard_access", ...], can_manage }
// A sub admin is view only and sees only the sections their role has. The API blocks the rest too,
// this only hides what they can't use.
import { getLmsMe } from "./lmsApi";

const ACCESS_KEY = "lms_access";

// menu sections in menu order, with the permission each needs
export const LMS_SECTIONS = [
  { to: "/lms", label: "Dashboard", permission: "lms_dashboard_access" },
  { to: "/lms/classes", label: "Classes & Schedule", permission: "lms_classes_access" },
  { to: "/lms/attendance", label: "Attendance", permission: "lms_attendance_access" },
  { to: "/lms/students", label: "Students", permission: "lms_students_access" },
  { to: "/lms/trainers", label: "Trainers", permission: "lms_trainers_access" },
  { to: "/lms/leave", label: "Leave & cancellations", permission: "lms_leaves_access" },
  { to: "/lms/cancellations", label: "Cancellation requests", permission: "lms_cancellation_requests_access" },
  { to: "/lms/broadcast", label: "Broadcast", permission: "lms_broadcast_access" },
];

// create / edit class forms and the sub admin screen, super admin only
const MANAGE_ONLY = [/^\/lms\/classes\/create\/?$/, /^\/lms\/classes\/[^/]+\/edit\/?$/, /^\/lms\/sub-admins(\/|$)/];

// cached per token (like the sidebar's roles), so screens render right away on reload
export const getLmsAccess = () => {
  try {
    const cached = JSON.parse(localStorage.getItem(ACCESS_KEY) || "null");
    return cached && cached.token === localStorage.getItem("token") ? cached.access : null;
  } catch {
    return null;
  }
};

export const saveLmsAccess = (access) => {
  try {
    localStorage.setItem(ACCESS_KEY, JSON.stringify({ token: localStorage.getItem("token"), access: access || null }));
  } catch {
    // private mode: access is loaded again from the API
  }
};

export const clearLmsAccess = () => {
  try {
    localStorage.removeItem(ACCESS_KEY);
  } catch {
    // nothing to clear
  }
};

// loads the latest access from the API and caches it
export const refreshLmsAccess = () =>
  getLmsMe().then(({ role, permissions, can_manage }) => {
    const access = { role, permissions: permissions || [], can_manage };
    saveLmsAccess(access);
    return access;
  });

export const isSubAdmin = (access = getLmsAccess()) => access?.role === "sub_admin";

// unknown access (not loaded yet / older API) counts as a super admin, the API still checks every call
export const canManage = (access = getLmsAccess()) => !isSubAdmin(access);

export const canSee = (section, access = getLmsAccess()) => !isSubAdmin(access) || access.permissions.includes(section.permission);

// the section a /lms path belongs to, longest match first ("/lms" itself is the dashboard)
const sectionFor = (path) =>
  [...LMS_SECTIONS].sort((a, b) => b.to.length - a.to.length).find((s) => path === s.to || path.startsWith(s.to + "/"));

// a sub admin can only open the LMS sections their role has, never the create / edit forms or the other admin screens
export const canOpen = (path, access = getLmsAccess()) => {
  if (!isSubAdmin(access)) return true;
  if (!path.startsWith("/lms") || MANAGE_ONLY.some((re) => re.test(path))) return false;
  const section = sectionFor(path);
  return Boolean(section) && canSee(section, access);
};

// first screen a sub admin is allowed to see
export const homePath = (access = getLmsAccess()) => (LMS_SECTIONS.find((s) => canSee(s, access)) || LMS_SECTIONS[0]).to;
